import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Modal, useToast } from '@morressier/alembic';

import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import {
  downloadExportManually,
  fetchSubmissionExportHistory,
  testExportConnections
} from 'src/api/queries';
import { exportConfigItem } from '@publishing-engine/domain/models';
import { useState } from 'react';
import { useRetrySubmissionExport } from 'src/api/mutations';
import { IVendorExportJobResponse } from '@publishing-engine/domain/models/vendor-exports';
import VendorExportConfigTable from 'src/components/VendorExport/VendorExportConfigTable';
import VendorExportHistoryTable from 'src/components/VendorExport/VendorExportHistoryTable';
import EditAndRetry from 'src/components/VendorExport/EditAndRetry';
import { renderBadge } from 'src/components/VendorExport/utils/VendorExportUtilities';
import { queryClient } from 'src/utils/queryClient';

const VendorExports = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [editItem, setEditItem] = useState<exportConfigItem | undefined>();
  const [page, setPage] = useState(1);
  const [retryLoadingMap, setRetryLoadingMap] = useState<
    Record<string, boolean>
  >({});
  const [editRetryLoadingMap, setEditRetryLoadingMap] = useState<
    Record<string, boolean>
  >({});
  const [downloadLoadingMap, setDownloadLoadingMap] = useState<
    Record<string, boolean>
  >({});

  const LIMIT = 10;
  const offset = (page - 1) * LIMIT;

  const { submissionContext } = useSubmissionContextData();
  const { submissionId, workflowResponse, organizationId } =
    useSubmissionData();
  const toaster = useToast();

  const { data: SubmissionExportHistory } = useQuery({
    queryKey: ['submission-export-history', { submissionId }, page],
    queryFn: () => fetchSubmissionExportHistory(submissionId, LIMIT, offset),
    retry: 0
  });
  const buttonsEnabled = () => {
    const state = workflowResponse?.workflowData?.currentState;
    return !state?.includes('formalAcceptance');
  };

  const { retrySubmissionExportAsync } = useRetrySubmissionExport(submissionId);
  const handleRetry = async (
    exportConfig: exportConfigItem,
    submissionId: string,
    submissionContextId: string,
    organizationId: string
  ) => {
    const id = exportConfig.id;
    setRetryLoadingMap(prev => ({ ...prev, [id]: true }));
    try {
      const connectionResponse = await testExportConnections(
        exportConfig.exportConfigType,
        JSON.stringify(exportConfig.serverInfo)
      );

      if (!connectionResponse.success) {
        toaster({
          variant: 'error',
          title: 'Connection failed',
          description: 'Please check your server details'
        });
        return;
      }
      toaster({
        variant: 'success',
        title: 'Connection successful',
        description: 'Server details verified'
      });
      toaster({
        variant: 'attention',
        title: `Retry Initiated`,
        description: 'Please wait for a while. Export is in progress'
      });
      const payloadToSend = [
        {
          exportConfig,
          submissions: [
            {
              submissionId,
              submissionContextId,
              organizationId
            }
          ],
          exportConfigId: exportConfig.id
        }
      ];

      const response: IVendorExportJobResponse =
        await retrySubmissionExportAsync(payloadToSend);

      if (response.isSuccess) {
        toaster({
          variant: 'success',
          title: 'Export successful',
          description: 'Submission exported successfully'
        });
      } else {
        toaster({
          variant: 'error',
          title: 'Export failed',
          description: 'Export failed. Please try again later'
        });
      }
    } catch (error) {
      toaster({
        variant: 'error',
        title: 'Connection failed',
        description: 'Please check your server details'
      });
    } finally {
      setRetryLoadingMap(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleEditAndRetry = async (item: exportConfigItem) => {
    const id = item.id;
    setEditRetryLoadingMap(p => ({ ...p, [id]: true }));
    setIsOpen(true);
    setEditItem(item);
  };

  const downloadVendorExport = useMutation<
    ArrayBuffer,
    Error,
    {
      submissionId: string;
      exportType: string;
      submissionContextId: string;
      exportName: string;
      exportConfigId: string;
    }
  >({
    mutationFn: ({
      submissionId,
      exportType,
      submissionContextId,
      exportName
    }: {
      submissionId: string;
      exportType: string;
      submissionContextId: string;
      exportName: string;
    }) =>
      downloadExportManually(
        submissionId,
        exportType,
        submissionContextId,
        exportName
      ),
    onMutate: ({ exportConfigId }) => {
      setDownloadLoadingMap(p => ({ ...p, [exportConfigId]: true }));
    },

    onSettled: (_, __, { exportConfigId }) => {
      setDownloadLoadingMap(p => ({ ...p, [exportConfigId]: false }));
    },
    onSuccess: data => {
      const blob = new Blob([data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `export_${workflowResponse.manuscriptId}.zip`;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      a.remove();
      queryClient.invalidateQueries({
        queryKey: ['submission-export-history', { submissionId }]
      });
      return toaster({
        variant: 'success',
        title: 'File downloaded successfully',
        isClosable: true
      });
    },

    onError: () => {
      queryClient.invalidateQueries({
        queryKey: ['submission-export-history', { submissionId }]
      });
      return toaster({
        variant: 'error',
        title: 'Error downloading file',
        isClosable: true
      });
    }
  });

  const total = SubmissionExportHistory ? SubmissionExportHistory.total : 0;

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <Box>
      <Flex gap={6} direction="column">
        <Flex flexDirection="column" gap={3} mr={5}>
          <Text textStyle="headingGroup">Configuration</Text>
          <Text textStyle="textBody">
            Configured daily data exports for vendor submissions.
          </Text>
          {submissionContext && (
            <VendorExportConfigTable
              submissionContext={submissionContext}
              workflowResponse={workflowResponse}
              submissionId={submissionId}
              handleEditAndRetry={handleEditAndRetry}
              handleRetry={handleRetry}
              downloadVendorExport={downloadVendorExport}
              buttonsEnabled={buttonsEnabled}
              retryLoadingMap={retryLoadingMap}
              editRetryLoadingMap={editRetryLoadingMap}
              downloadLoadingMap={downloadLoadingMap}
            />
          )}
        </Flex>

        <Flex flexDirection="column" gap={3} mr={5}>
          <Text textStyle="headingGroup">History</Text>
          <Text textStyle="textBody">View a record of all past exports.</Text>
          {SubmissionExportHistory?.data && (
            <VendorExportHistoryTable
              paginatedData={SubmissionExportHistory}
              renderBadge={renderBadge}
              total={total}
              totalPages={totalPages}
              page={page}
              setPage={setPage}
            />
          )}
        </Flex>
      </Flex>
      {editItem && (
        <Modal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setEditRetryLoadingMap(p => ({ ...p, [editItem?.id]: false }));
          }}
          width="720px"
          closeButtonProps={{
            hidden: false
          }}
        >
          <EditAndRetry
            item={editItem}
            submissionId={submissionId}
            submissionContextId={submissionContext?.id || ''}
            setIsOpen={setIsOpen}
            setEditRetryLoadingMap={setEditRetryLoadingMap}
            organizationId={organizationId}
          />
        </Modal>
      )}
    </Box>
  );
};

export default VendorExports;
